import Promotion from '@/models/promotion';
import axios from 'axios';

export class PromotionsController {

    /**
     * Get the available list of promotions
     *
     * @returns {Array|Error} An array of Promotion models or an error object
     */
    static async index() {
        let response = axios
            .get('promotions')
            .then((resp) => resp.data.map((promotion) => new Promotion(promotion)))
            .catch((error) => Promise.reject(error));
        return await response;
    }

    /**
     * Get a single promotion by ID
     *
     * @param {Promotion|int} $promotion The promotion model or ID
     * @returns {Promotion|Error} A Promotion model or an error object
     */
    static async show($promotion) {
        const id = $promotion?.id ?? $promotion;
        let response = axios
            .get(`promotions/${id}`)
            .then((resp) => new Promotion(resp.data))
            .catch((error) => Promise.reject(error));
        return await response;
    }

    /**
     * Create a new promotion
     *
     * @param {Promotion} $promotion The promotion model to create
     * @returns {Promotion|Error} A Promotion model or an error object
     */
    static async store($promotion) {
        const payload = PromotionsController._payload($promotion);
        let response = axios
            .postForm('promotions', payload)
            .then((resp) => new Promotion(resp.data))
            .catch((error) => Promise.reject(error));
        return await response;
    }

    /**
     * Update an existing promotion
     *
     * @param {Promotion} $promotion The promotion model to update
     * @returns {Promotion|Error} A Promotion model or an error object
     */
    static async update($promotion) {
        const payload = PromotionsController._payload($promotion);
        let response = axios
            .postForm(`promotions/${$promotion.id}`, { ...payload, _method: 'PATCH' })
            .then((resp) => new Promotion(resp.data))
            .catch((error) => Promise.reject(error));
        return await response;
    }

    /**
     * Delete a promotion
     *
     * @param {Promotion|int} $promotion The promotion model or ID
     * @returns {void|Error} Nothing on success or an error object
     */
    static async delete($promotion) {
        const id = $promotion?.id ?? $promotion;
        let response = axios
            .delete(`promotions/${id}`)
            .then(() => null)
            .catch((error) => Promise.reject(error));
        return await response;
    }

    /**
     * Build the FormData-compatible payload from a promotion model
     *
     * @param {Promotion} $promotion
     * @returns {Object}
     */
    static _payload($promotion) {
        return {
            headline: $promotion.headline,
            type: $promotion.type,
            button_text: $promotion.button_text,
            open_in_new_tab: $promotion.open_in_new_tab ? 1 : 0,
            description: $promotion.description,
            disclaimer: $promotion.disclaimer,
            start_at: $promotion.start_at ? new Date($promotion.start_at).toISOString() : '',
            end_at: $promotion.end_at ? new Date($promotion.end_at).toISOString() : '',
            url: $promotion.url,
            file: $promotion.file,
            meta: {
                ...$promotion.meta,
                show_image_inline: $promotion.meta?.show_image_inline ? 1 : 0,
            },
        };
    }
}
